/**
 * The bridge between a Session and the engine: the extra Entries a Session feeds the engine, the
 * maxima the sheet yields, and the grants Session items ask for (dying grants unconscious, an
 * Effect grants a Condition), materialised into the Session the way src/character/grants.ts
 * materialises progression grants.
 */
import type { Entry } from "../../shared/snapshot-format";
import type { Character } from "../character/model";
import type { Catalogue } from "../engine/catalogue";
import type { DerivedSheet, EngineInput } from "../engine/engine";
import type { EngineItemInput } from "../engine/items";
import type { PrimitiveAction } from "./actions";
import type { SessionMaxima, SpellcastingSummary } from "./maxima";
import { emptyMaxima } from "./maxima";
import type { ConsumableStack, DurationUnit, EffectDuration, ExpiryType, Session, SessionCondition, SessionEffect } from "./model";
import { DURATION_UNITS, remainingDuration } from "./model";

/** What a Session adds to an engine derive: its Conditions and unexpired Effects, and its toggle states. */
export function sessionEngineInput(session: Session | null, entries: ReadonlyMap<string, Entry>): Pick<EngineInput, "extra" | "toggleState"> {
  if (!session) return {};
  const extra: EngineItemInput[] = [];
  for (const c of session.conditions) {
    const entry = entries.get(c.uuid);
    if (!entry) continue;
    extra.push({ entry, sessionId: c.id, grantedBySessionId: c.grant?.grantedBy, badge: c.value ?? undefined, persistent: c.persistent ?? undefined });
  }
  for (const e of session.effects) {
    const entry = entries.get(e.uuid);
    if (!entry || remainingDuration(e, session.clock).expired) continue;
    extra.push({ entry, sessionId: e.id, grantedBySessionId: e.grant?.grantedBy, badge: e.badge ?? undefined, level: e.level ?? undefined });
  }
  return { extra, toggleState: session.toggles };
}

/** Every Entry UUID a Session names. */
export function sessionUuids(session: Session | null): string[] {
  if (!session) return [];
  return [...new Set([...session.conditions.map((c) => c.uuid), ...session.effects.map((e) => e.uuid)])];
}

/** An Effect Entry's duration as the Session records it. */
export function durationOfEntry(entry: Entry): EffectDuration {
  const d = entry.system.duration as { unit?: unknown; value?: unknown; expiry?: unknown; sustained?: unknown } | undefined;
  const unit = typeof d?.unit === "string" && (DURATION_UNITS as readonly string[]).includes(d.unit) ? (d.unit as DurationUnit) : "unlimited";
  const expiry: ExpiryType = d?.expiry === "turn-end" ? "turn-end" : d?.expiry === "turn-start" ? "turn-start" : null;
  return { unit, value: typeof d?.value === "number" && d.value > 0 ? d.value : unit === "rounds" || unit === "minutes" || unit === "hours" || unit === "days" ? 1 : 0, expiry, sustained: d?.sustained === true };
}

/** The counter badge value an Effect Entry starts with, if it has one. */
export function badgeOfEntry(entry: Entry): number | null {
  const badge = entry.system.badge as { type?: unknown; value?: unknown } | null | undefined;
  return badge && typeof badge === "object" && badge.type === "counter" && typeof badge.value === "number" ? badge.value : null;
}

/**
 * Make the Session hold what its items grant: add wanted grants not yet held, remove granted
 * items whose grant is no longer wanted or whose granter has gone (cascade) or detach them.
 * Pure; returns the same Session when nothing changed.
 */
export function reconcileSession(session: Session, sheet: DerivedSheet): { session: Session; changed: boolean } {
  let counter = session.counter;
  let conditions = [...session.conditions];
  let effects = [...session.effects];
  let changed = false;
  const ids = new Set([...conditions.map((c) => c.id), ...effects.map((e) => e.id), ...session.consumables.map((s) => s.id)]);
  const allocate = (prefix: string): string => {
    let id = `${prefix}${counter++}`;
    while (ids.has(id)) id = `${prefix}${counter++}`;
    ids.add(id);
    return id;
  };

  // Granted items whose granter is gone.
  const orphan = <T extends SessionCondition | SessionEffect>(item: T): T | null => {
    if (!item.grant || ids.has(item.grant.grantedBy)) return item;
    changed = true;
    return item.grant.onDelete.granter === "detach" ? { ...item, grant: null } : null;
  };
  conditions = conditions.map(orphan).filter((c): c is SessionCondition => c !== null);
  effects = effects.map(orphan).filter((e): e is SessionEffect => e !== null);

  // Grants no longer wanted (the granter's predicate fails now).
  const wantedKeys = new Set(sheet.temporaryGrants.filter((g) => g.wanted && g.uuid).map((g) => `${g.granterId}:${g.uuid}`));
  const askedKeys = new Set(sheet.temporaryGrants.filter((g) => g.uuid).map((g) => `${g.granterId}:${g.uuid}`));
  const stillWanted = <T extends SessionCondition | SessionEffect>(item: T): boolean => {
    if (!item.grant) return true;
    const key = `${item.grant.grantedBy}:${item.uuid}`;
    // Only grants the sheet knows about are judged; a granter whose Entry is missing keeps its grants.
    if (!askedKeys.has(key) || wantedKeys.has(key)) return true;
    changed = true;
    return false;
  };
  conditions = conditions.filter(stillWanted);
  effects = effects.filter(stillWanted);

  // Wanted grants not held.
  for (const grant of sheet.temporaryGrants) {
    if (!grant.wanted || !grant.uuid || !grant.loaded || !grant.type || !grant.slug) continue;
    const held = grant.allowDuplicate
      ? [...conditions, ...effects].some((i) => i.grant?.grantedBy === grant.granterId && i.uuid === grant.uuid)
      : [...conditions, ...effects].some((i) => i.uuid === grant.uuid);
    if (held) continue;
    const link = { grantedBy: grant.granterId, onDelete: grant.onDelete };
    if (grant.type === "condition") {
      conditions.push({ id: allocate("c"), uuid: grant.uuid, slug: grant.slug, name: grant.name ?? grant.slug, value: grant.badge, grant: link, persistent: null });
    } else {
      const d = grant.duration;
      const unit = d && (DURATION_UNITS as readonly string[]).includes(d.unit) ? (d.unit as DurationUnit) : "unlimited";
      effects.push({ id: allocate("e"), uuid: grant.uuid, slug: grant.slug, name: grant.name ?? grant.slug, level: null, badge: grant.badge, start: session.clock, duration: { unit, value: d?.value ?? 0, expiry: d?.expiry === "turn-end" ? "turn-end" : d?.expiry === "turn-start" ? "turn-start" : null, sustained: d?.sustained ?? false }, grant: link });
    }
    changed = true;
  }
  return changed ? { session: { ...session, conditions, effects, counter }, changed } : { session, changed };
}

/**
 * Primitive steps that turn `to` back into `from`: what `reconcileSession` (or any other change
 * outside a Play Action) did, expressed so it can join a Play Action's inverse. Items are matched
 * by id; a changed item is removed and re-added as it was.
 */
export function sessionDelta(from: Session, to: Session): PrimitiveAction[] {
  const steps: PrimitiveAction[] = [];
  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  const fromConditions = new Map(from.conditions.map((c) => [c.id, c]));
  const toConditions = new Map(to.conditions.map((c) => [c.id, c]));
  for (const c of to.conditions) if (!same(fromConditions.get(c.id), c)) steps.push({ kind: "remove-condition", id: c.id });
  from.conditions.forEach((c, index) => {
    if (!same(toConditions.get(c.id), c)) steps.push({ kind: "add-condition", condition: structuredClone(c), index });
  });
  const fromEffects = new Map(from.effects.map((e) => [e.id, e]));
  const toEffects = new Map(to.effects.map((e) => [e.id, e]));
  for (const e of to.effects) if (!same(fromEffects.get(e.id), e)) steps.push({ kind: "remove-effect", id: e.id });
  from.effects.forEach((e, index) => {
    if (!same(toEffects.get(e.id), e)) steps.push({ kind: "add-effect", effect: structuredClone(e), index });
  });
  if (from.counter !== to.counter) steps.push({ kind: "set-counter", value: from.counter });
  return steps;
}

/**
 * The maxima a Session is measured against, from the derived sheet (with the Session's own
 * Entries applied, so a Condition that lowers max HP counts), the Entries held (for
 * frequencies) and the catalogue (for the conditions Pack).
 */
export function maximaFromSheet(character: Character, sheet: DerivedSheet, entries: ReadonlyMap<string, Entry>, catalogue: Catalogue | null): SessionMaxima {
  const uses: SessionMaxima["uses"] = {};
  for (const item of sheet.items) {
    if (item.sessionId || item.inMemory) continue;
    const entry = entries.get(item.uuid);
    const frequency = entry?.system.frequency as { max?: unknown; per?: unknown } | null | undefined;
    if (frequency && typeof frequency === "object" && typeof frequency.max === "number" && frequency.max > 0 && typeof frequency.per === "string") {
      uses[item.uuid] = { name: item.name, max: frequency.max, per: frequency.per };
    }
  }
  const conditions: SessionMaxima["conditions"] = {};
  for (const record of catalogue?.records ?? []) {
    if (record.t === "condition" && record.p === "conditionitems") conditions[record.s] = { uuid: record.u, name: record.n, valued: VALUED_CONDITIONS.has(record.s) };
  }
  const resources: SessionMaxima["resources"] = {};
  for (const [key, r] of Object.entries(sheet.resources.special)) resources[key] = { label: r.label, max: r.max, renew: r.renew };
  const spellcasting = spellcastingMaxima(sheet);
  const consumables = consumableStacks(sheet, entries);
  return emptyMaxima({
    level: sheet.level,
    hp: { max: sheet.hp.value, recoveryMultiplier: sheet.hp.recoveryMultiplier, recoveryAddend: sheet.hp.recoveryAddend },
    conMod: sheet.abilities.con.value,
    heroPoints: { max: sheet.resources.heroPoints.max },
    focus: { max: sheet.resources.focus.max },
    death: { dying: sheet.death.dying.max + sheet.death.doomed.value, wounded: sheet.death.wounded.max, doomed: sheet.death.doomed.max },
    resources,
    uses,
    spellcasting,
    consumables,
    conditions,
    fastHealing: sheet.resources.fastHealing.map((f) => ({ value: f.value, enabled: f.enabled, sourceName: f.sourceName })),
    tempHPOnTurnStart: sheet.resources.tempHP.filter((t) => t.events.onTurnStart).map((t) => ({ value: t.value, enabled: t.enabled, sourceName: t.sourceName })),
    iwr: structuredClone(sheet.iwr),
    negativeHealing: sheet.hp.negativeHealing,
  });
  void character;
}

/**
 * The Session's view of the Spellcasting Entries (#10): ranked slots with their maxima (cantrips
 * and focus spells are at will and are not slots), what is prepared in each, and innate uses per
 * day by spell (an at-will innate spell has no entry).
 */
export function spellcastingMaxima(sheet: DerivedSheet): SpellcastingSummary[] {
  const out: SpellcastingSummary[] = [];
  for (const entry of sheet.spellcasting) {
    const slots: SpellcastingSummary["slots"] = {};
    if (entry.casting === "prepared" || entry.casting === "spontaneous") {
      for (const [rank, max] of Object.entries(entry.slots)) {
        if (rank === "0" || max <= 0) continue;
        slots[rank] = entry.casting === "prepared" ? { max, prepared: (entry.prepared[rank] ?? []).map((s) => s?.uuid ?? null) } : { max };
      }
    }
    const innate: SpellcastingSummary["innate"] = {};
    if (entry.casting === "innate") {
      for (const spell of entry.spells) if (spell.uses !== null && spell.uses > 0) innate[spell.uuid] = { max: spell.uses, name: spell.name };
    }
    // A staff (#21): its charges, and the spells that cost them by rank (cantrips are free and listed at 0).
    const charges = entry.casting === "items" && entry.charges !== null ? { max: entry.charges, spells: entry.spells.map((s) => ({ uuid: s.uuid, name: s.name, rank: s.cantrip ? 0 : s.rank })) } : undefined;
    if (Object.keys(slots).length === 0 && Object.keys(innate).length === 0 && !charges) continue;
    out.push({ id: entry.id, name: entry.name, casting: entry.casting, slots, innate, ...(charges ? { charges } : {}) });
  }
  return out;
}

/**
 * The inventory's consumable and ammunition stacks as the Session counts them (#11): one stack
 * per inventory item of those types, its id the inventory item's, so a Session started fresh
 * draws on what the Character carries. Play-time quantities live on the Session copy; the
 * inventory keeps what the Character owns between Sessions.
 */
export function consumableStacks(sheet: DerivedSheet, entries: ReadonlyMap<string, Entry>): ConsumableStack[] {
  const out: ConsumableStack[] = [];
  for (const item of sheet.inventory.items) {
    if (item.type !== "consumable" && item.type !== "ammo") continue;
    const entry = entries.get(item.uuid);
    const uses = entry?.system.uses as { value?: unknown; max?: unknown; autoDestroy?: unknown } | undefined;
    const max = typeof uses?.max === "number" && uses.max > 0 ? uses.max : 1;
    const wand = item.type === "consumable" && (entry?.system.category === "wand" || uses?.autoDestroy === false);
    out.push({
      id: item.id,
      uuid: item.uuid,
      name: item.name,
      quantity: item.quantity,
      uses: max > 1 || wand ? { value: max, max, renew: wand ? "day" : null } : null,
      autoDestroy: uses?.autoDestroy !== false,
      ammunition: item.type === "ammo",
    });
  }
  return out;
}

/** Foundry's valued Conditions (`system.value.isValued` in the conditions Pack). */
export const VALUED_CONDITIONS: ReadonlySet<string> = new Set(["clumsy", "cursebound", "doomed", "drained", "dying", "enfeebled", "frightened", "sickened", "slowed", "stunned", "stupefied", "wounded"]);

/** Temporary HP an Entry grants on arrival (Foundry: TempHP `onCreate`), the highest enabled value for that source. */
export function tempHPOnArrival(sheet: DerivedSheet, uuid: string): number | null {
  const values = sheet.resources.tempHP.filter((t) => t.source === uuid && t.enabled && t.events.onCreate).map((t) => t.value);
  return values.length > 0 ? Math.max(...values) : null;
}
